import { useState } from "react";
import { useTranslation } from "react-i18next";
import Spinner from "./common/Spinner";
import Toast from "./common/Toast";
import { useApi } from "../store/hooks";

const PurchasersInvitation = ({
  isOpen,
  onClose,
  onSuccess,
  invitees,
  setInvitees,
  companyName,
}) => {
  const [errors, setErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [showTechnicalError, setShowTechnicalError] = useState(false);
  const [showDuplicateError, setShowDuplicateError] = useState(false);
  const { t } = useTranslation();
  const { inviteCollaborators } = useApi();

  const inviteCount = invitees.length;

  const validateEmail = (email) => {
    if (!email) return false;

    // Prevent ReDoS by checking length first
    if (email.length > 254) return false;

    // Simple validation: must contain @ and at least one dot after @
    const atIndex = email.indexOf("@");
    if (atIndex === -1 || atIndex === 0 || atIndex === email.length - 1) {
      return false;
    }

    const dotAfterAt = email.indexOf(".", atIndex);
    if (dotAfterAt === -1 || dotAfterAt === email.length - 1) {
      return false;
    }

    // Check for invalid characters (whitespace)
    if (/\s/.test(email)) {
      return false;
    }

    return true;
  };

  const validateInvitees = () => {
    const newErrors = invitees.map((invitee) => {
      const error = { firstName: false, lastName: false, email: false };

      if (!invitee.firstName) error.firstName = true;
      if (!invitee.lastName) error.lastName = true;
      if (!invitee.email) {
        error.email = true;
      } else if (!validateEmail(invitee.email)) {
        error.email = true;
      }

      return error;
    });

    setErrors(newErrors);
    return newErrors.every(
      (error) => !error.firstName && !error.lastName && !error.email,
    );
  };

  const handleInviteeChange = (index, field, value) => {
    setInvitees((currentInvitees) =>
      currentInvitees.map((invitee, currentIndex) =>
        currentIndex === index ? { ...invitee, [field]: value } : invitee,
      ),
    );
    if (errors[index]) {
      setErrors((currentErrors) =>
        currentErrors.map((error, currentIndex) =>
          currentIndex === index ? { ...error, [field]: false } : error,
        ),
      );
    }

    if (showValidationError) {
      setShowValidationError(false);
    }
  };

  const addInvitee = () => {
    setInvitees((currentInvitees) => [
      ...currentInvitees,
      { firstName: "", lastName: "", email: "" },
    ]);
    setErrors((currentErrors) => [
      ...currentErrors,
      { firstName: false, lastName: false, email: false },
    ]);
  };

  const removeInvitee = (index) => {
    setInvitees((currentInvitees) =>
      currentInvitees.filter((_, currentIndex) => currentIndex !== index),
    );
    setErrors((currentErrors) =>
      currentErrors.filter((_, currentIndex) => currentIndex !== index),
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateInvitees()) {
      setShowValidationError(true);
      return;
    }

    setShowValidationError(false);
    setShowTechnicalError(false);
    setShowDuplicateError(false);
    setIsSubmitting(true);

    try {
      const payload = invitees.map((invitee) => ({
        firstName: invitee.firstName,
        lastName: invitee.lastName,
        email: invitee.email,
      }));

      await inviteCollaborators(payload);
      onSuccess();
    } catch (error) {
      if (error.response?.data?.Code === "110028") {
        setShowDuplicateError(true);
      } else {
        setShowTechnicalError(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="success-modal-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="success-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-team-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="success-modal-close"
          onClick={onClose}
          aria-label={t("successPage.modal.close")}
        >
          ×
        </button>

        <h2 id="invite-team-modal-title" className="success-modal-title">
          {t("successPage.modal.title")}
        </h2>

        <p className="success-modal-description">
          {t("successPage.modal.description1", { companyName })}
        </p>
        <p className="success-modal-description">
          {t("successPage.modal.description2")}
        </p>

        <div className="success-modal-users">
          {invitees.map((invitee, index) => (
            <div key={`invitee-${index}`} className="success-modal-row">
              <div
                className={`success-modal-input-group ${errors[index]?.firstName ? "az-formfield--error" : ""}`}
              >
                <label htmlFor={`fn-${index}`}>
                  {t("successPage.modal.firstName")}
                </label>
                <input
                  id={`fn-${index}`}
                  type="text"
                  name={`fn-${index}-${Date.now()}`}
                  value={invitee.firstName}
                  onChange={(event) =>
                    handleInviteeChange(index, "firstName", event.target.value)
                  }
                  disabled={isSubmitting}
                  data-lpignore="true"
                  data-form-type="other"
                  data-1p-ignore="true"
                  autoComplete="nope"
                />
              </div>

              <div
                className={`success-modal-input-group ${errors[index]?.lastName ? "az-formfield--error" : ""}`}
              >
                <label htmlFor={`ln-${index}`}>
                  {t("successPage.modal.lastName")}
                </label>
                <input
                  id={`ln-${index}`}
                  type="text"
                  name={`ln-${index}-${Date.now()}`}
                  value={invitee.lastName}
                  onChange={(event) =>
                    handleInviteeChange(index, "lastName", event.target.value)
                  }
                  disabled={isSubmitting}
                  data-lpignore="true"
                  data-form-type="other"
                  data-1p-ignore="true"
                  autoComplete="nope"
                />
              </div>

              <div
                className={`success-modal-input-group ${errors[index]?.email ? "az-formfield--error" : ""}`}
              >
                <label htmlFor={`em-${index}`}>E-mail</label>
                <input
                  id={`em-${index}`}
                  type="text"
                  name={`em-${index}-${Date.now()}`}
                  value={invitee.email}
                  onChange={(event) =>
                    handleInviteeChange(index, "email", event.target.value)
                  }
                  disabled={isSubmitting}
                  data-lpignore="true"
                  data-form-type="other"
                  data-1p-ignore="true"
                  autoComplete="new-password"
                />
              </div>

              {invitees.length > 1 && (
                <button
                  type="button"
                  className="success-modal-delete-user"
                  onClick={() => removeInvitee(index)}
                  aria-label={t("successPage.modal.removeUser")}
                  title={t("successPage.modal.removeUser")}
                  disabled={isSubmitting}
                >
                  ×
                </button>
              )}
            </div>
          ))}
          {invitees.length < 5 && (
            <button
              type="button"
              className="success-modal-add-user"
              onClick={addInvitee}
              disabled={isSubmitting}
            >
              <span className="success-modal-add-user-icon">+</span>
              <span className="success-modal-add-user-label">
                {t("successPage.modal.addUser")}
              </span>
            </button>
          )}

          {showValidationError && (
            <Toast
              text={t("successPage.modal.validationError")}
              isError={true}
            />
          )}
          {showTechnicalError && (
            <Toast text={t("successPage.modal.submitError")} isError={true} />
          )}
          {showDuplicateError && (
            <Toast
              text={t("successPage.modal.duplicateError")}
              isError={true}
            />
          )}
        </div>

        <div className="success-modal-actions">
          <button
            type="button"
            className="success-secondary-btn"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t("successPage.modal.cancel")}
          </button>

          <button
            type="button"
            className={`az-btn success-primary-btn text-uppercase ${isSubmitting ? "disabled" : ""}`}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Spinner size={24} />
            ) : (
              t("successPage.modal.invite", { count: inviteCount })
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PurchasersInvitation;
